import classNames from 'classnames';
import { format } from 'date-fns';
import jsCookie from 'js-cookie';
import isUndefined from 'lodash.isundefined';
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';

// components
import {
    BreadCrumbContainerStyled,
    BodyContainerStyled,
    GridContainerStyled,
    CardsSavedContainerStyled,
    FooterMobileContainerStyled,
} from './CartPaymentPageInner.styled';
import CartPaymentElementWrapper from './CartPaymentElementWrapper';

// import CardNameForm from './CardNameForm';
import CardsSaved from './CardsSaved';
import FooterNavigation from '../../../_molecules/FooterNavigation';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import CartSummarySidebar from '../../../_organisms/CartSummarySidebar';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import RadioWithoutForm from '@atoms/RadioWithoutForm';
import NotificationBanner, { notificationTypes } from '@molecules/NotificationBanner';
import StepperNavMobile from '@molecules/StepperNavMobile';
import {
    BodyMedium,
    BodySmall,
    ButtonPrimaryLarge,
    DividerBlueStyled,
    DivWithShadowAlternate,
    H3,
    H5,
    LinkText,
} from '@styled';
import TooltipInfo from '@atoms/TooltipInfo/TooltipInfo';
import colors from '@general/colors';

// functionality
import { LoadingContext } from '@context/LoadingProvider';
import { savePaymentMethod } from '@general/api/billingAPI';
import { checkOutCart, getCartById } from '@general/api/cartAPI';
import { updateMember } from '@general/api/memberAPI';
import { getDoesCartHaveMemberships } from '@general/helpers/cart.helpers';
import { formatMoney } from '@general/helpers/currency.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useStripeCustomerFromAPI from '@hooks/apiLegacy/useStripeCustomerFromAPI';
import useOauthUserInfo from '@hooks/useOauthUserInfo';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useStripeInstanceForCart from '@hooks/useStripeInstanceForCart';
import useViewer from '@hooks/useViewer';
import useIcscTracking from '@react/tracking/useIcscTracking';
import { getMemberInfoBreadcrumbLink } from '@general/helpers/member.helpers';
import { getLoginPath } from '../../../../helpers/url.helpers';

const CartPaymentPageInner = () => {
    // -----------------------------
    // State
    // -----------------------------

    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isAutoRenewChecked, setIsAutoRenewChecked] = useState(true);
    const [paidThruText, setPaidThruText] = useState();
    const [stripeCardComplete, setStripeCardComplete] = useState(false);
    const [stripeAddressComplete, setSetStripeAddressComplete] = useState(false);
    const [conditionsAccepted, setConditionsAccepted] = useState(true);
    const [errorMessage, setErrorMessage] = useState();
    const [stripeElements, setStripeElements] = useState();
    const [selectedPayment, setSelectedPayment] = useState();

    // -----------------------------
    // Hooks
    // -----------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const history = useHistory();
    const { Track } = useIcscTracking({
        app_area: 'Cart Payment',
    });

    const { viewer } = useViewer();
    const { userInfo } = useOauthUserInfo();
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { stripeCustomer } = useStripeCustomerFromAPI({ locale: cart?.locale, memberId: viewer?.recordId });
    const { stripeInstanceForCart } = useStripeInstanceForCart();
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (isFirstLoad) {
            setIsLoading(true);
        }
    }, [isFirstLoad, setIsLoading]);

    useEffect(() => {
        if (getIsReadyForRender()) {
            setIsLoading(false);
        }
    }, [getIsReadyForRender, setIsLoading, cart, stripeCustomer, selectedPayment]);

    useEffect(() => {
        if (!stripeCustomer || !setSelectedPayment) {
            return;
        }

        setSelectedPayment(stripeCustomer.cards[0]?.id || 'new');
    }, [stripeCustomer, setSelectedPayment]);

    useEffect(() => {
        if (!viewer) {
            return;
        }

        if (viewer.paidThru) {
            const paidThruSegments = viewer.paidThru.split('-');
            setPaidThruText(`${paidThruSegments[1]}/${paidThruSegments[2]}`);
        } else {
            setPaidThruText(format(new Date(), 'MM/dd'));
        }
    }, [viewer]);

    // -----------------------------
    // Functions
    // -----------------------------

    const savePaymentNotification = useMemo(() => {
        const defaultMsgs = {
            label: 'Securely store payment method for future use on ICSC.',
            tooltip: 'Your credit card information will be securely stored in your member profile',
        };
        const autorenewMsgs = {
            label: 'Securely store payment method and enroll in membership Auto-Renew.',
            tooltip:
                'Auto-renew is a convenient and seamless way to renew your ICSC membership annually. Your credit card information will be securely stored in your member profile and you will be notified about your renewal term date in advance of payment processing. An email receipt of your membership transaction will be provided to you for your records.',
        };

        if (cart == null || viewer == null) return defaultMsgs;

        // if the cart has a product of 'dues' product_type, return autorenew message
        return cart.items.reduce((msg, item) => {
            if (item.product_type === 'dues' && item.memberId === viewer.recordID) {
                return autorenewMsgs;
            }
            return msg;
        }, defaultMsgs);
    }, [cart, viewer]);

    const getIsReadyForRender = useCallback(() => {
        if (!cart || !stripeCustomer || isUndefined(selectedPayment)) {
            return false;
        }

        return true;
    }, [cart, stripeCustomer, selectedPayment]);

    const getIsSavedCardSelected = () => {
        return !!selectedPayment && selectedPayment !== 'new';
    };

    const getIsNewCardReady = () => {
        // return selectedPayment === 'new' && !!firstNameOnCard && !!lastNameOnCard && stripeFormComplete;
        // return selectedPayment === 'new' && stripeCardComplete && stripeAddressComplete;
        return selectedPayment === 'new' && stripeCardComplete;
    };

    const confirmStripePayment = async () => {
        const clientSecret = cart.payment_intent?.client_secret;
        if (!clientSecret) {
            return null;
        }

        if (selectedPayment !== 'new') {
            const cardPaymentData = {
                payment_method: selectedPayment,
            };

            const { error } = await stripeInstanceForCart.confirmCardPayment(clientSecret, cardPaymentData);
            if (error) {
                return null;
            }
        } else {
            const { error } = await stripeInstanceForCart.confirmPayment({
                elements: stripeElements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (error) {
                return null;
            }

            return true;
        }

        return true;
    };

    const checkOut = async () => {
        setIsLoading(true);
        setErrorMessage();

        let freshCart = null;

        try {
            freshCart = await getCartById({ cartId: cart.id });
        } catch (error) {
            setErrorMessage(error);
            console.error(error);
            setIsLoading(false);
            return;
        }

        if (freshCart.items.length === 0) {
            refetch();
            setIsLoading(false);
            return;
        }

        if (freshCart.order && freshCart.order.completed_at) {
            setErrorMessage(
                'There has been an error while processing your cart. Your order has already been completed.'
            );
            setIsLoading(false);
            return;
        }

        if (freshCart.errors?.items && Object.keys(freshCart.errors.items).length > 0) {
            setErrorMessage(
                'There has been an error while processing your cart. Please return to the cart page to resolve the issue(s).'
            );
            setIsLoading(false);
            return;
        }

        try {
            if (cart.locale === 'US' && isAutoRenewChecked && cart.total !== 0) {
                if (getDoesCartHaveMemberships({ cart, memberId: viewer?.recordId })) {
                    await updateMember(viewer?.recordId, { auto_renew: true });
                }
            }

            if (isAutoRenewChecked && cart.total !== 0) {
                await savePaymentMethod();
            }

            if (cart.total !== 0) {
                const success = await confirmStripePayment();
                if (!success) {
                    setErrorMessage(
                        'There has been an error while processing your cart. Your payment method could not be confirmed.'
                    );

                    setIsLoading(false);
                    return;
                }
            }

            const cartResponse = await checkOutCart({ cartId: cart.id, viewerId: viewer.recordId });
            if (!cartResponse) {
                setErrorMessage('There has been an error while processing your cart.');
                setIsLoading(false);
                return;
            }

            const orderReceipt = JSON.stringify({
                appliedStoreCredit: cart._calculated?.appliedStoreCredit,
                appliedEventCredit: cart._calculated?.appliedEventCredit,
                tax: cart._calculated?.tax,
            });
            window.localStorage.setItem('orderReceipt', orderReceipt);

            jsCookie.set('cart_id_completed', cart.id);
            history.push('/cart/confirmation');

            if (userInfo === null) {
                window.location.href = getLoginPath();
            }
        } catch (e) {
            setErrorMessage(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const rules = {
        '.Input::placeholder': {
            color: colors.mediumGray,
        },
    };

    if (selectedPayment === 'new') {
        const noBorder = {
            borderColor: '#dddddd',
            boxShadow: 'none',
        };

        const disabledText = {
            color: colors.mediumGray,
        };

        const cursorNotAllowed = {
            cursor: 'not-allowed',
        };

        // Tab
        rules['.Tab'] = disabledText;
        rules['.Tab:focus'] = noBorder;
        rules['.Tab:hover'] = {
            ...disabledText,
        };

        rules['.Tab--selected'] = noBorder;
        rules['.Tab--selected:focus'] = noBorder;

        // TabIcon
        rules['.TabIcon--selected'] = {
            fill: colors.mediumGray,
        };

        rules['.TabIcon--selected:hover'] = {
            ...cursorNotAllowed,
        };

        // TabLabel
        rules['.TabLabel:focus'] = disabledText;
        rules['.TabLabel:hover'] = disabledText;
        rules['.TabLabel--selected'] = disabledText;

        // Input
        rules['.Input:focus'] = noBorder;

        // AccordionItem
        rules['.AccordionItem'] = disabledText;
        rules['.AccordionItem:focus'] = disabledText;
        rules['.AccordionItem:hover'] = disabledText;
        rules['.AccordionItem--selected'] = disabledText;
    }

    // -----------------------------
    // Render
    // -----------------------------

    if (!getIsReadyForRender()) {
        return null;
    }

    const isConfirmButtonDisabled =
        !conditionsAccepted || (cart?.total > 0 && !getIsSavedCardSelected() && !getIsNewCardReady());

    return (
        <Track>
            <div>
                {isScreenMinMd && (
                    <BreadCrumbContainerStyled>
                        <div className="container">
                            <div className="py-1rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb route={getMemberInfoBreadcrumbLink(cart, viewer?.recordId)}>
                                        Account Info
                                    </SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/view">Cart Details</SectionBreadcrumb>
                                    <SectionBreadcrumb isActive>Payment Info</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>
                        </div>
                    </BreadCrumbContainerStyled>
                )}

                {isScreenSmDown && (
                    <StepperNavMobile
                        activeIndex={2}
                        steps={[
                            { label: 'Account Info', href: getMemberInfoBreadcrumbLink(cart, viewer?.recordId) },
                            { label: 'Cart Details', href: '/cart/view' },
                            { label: 'Payment Info', href: '/cart/payment' },
                        ]}
                    />
                )}

                <BodyContainerStyled className="container">
                    <div>
                        <H3 className="mb-0.25rem">Payment Information</H3>

                        {isScreenMinMd && <BodyMedium className="mt-0.25rem">Select your payment method.</BodyMedium>}
                        {isScreenSmDown && <BodySmall className="mt-0.5rem">Select your payment method.</BodySmall>}

                        <GridContainerStyled>
                            <div className="col-span-5">
                                {errorMessage && (
                                    <div className="mb-0.625rem">
                                        <NotificationBanner notificationType={notificationTypes.ERROR}>
                                            {errorMessage}
                                        </NotificationBanner>
                                    </div>
                                )}

                                <DividerBlueStyled className="mb-0 mt-0" />

                                {stripeCustomer.cards.length > 0 && (
                                    <CardsSavedContainerStyled className="mt-2.5rem">
                                        <H5 className="mb-1rem">Your saved options</H5>
                                        <CardsSaved
                                            locale={cart.locale}
                                            selectedPayment={selectedPayment}
                                            onSelect={id => setSelectedPayment(id)}
                                        />
                                    </CardsSavedContainerStyled>
                                )}

                                <div className="mt-2.5rem">
                                    {stripeCustomer.cards.length > 0 && (
                                        <H5 className="mb-1rem">Other payment options</H5>
                                    )}

                                    {cart.total !== 0 && (
                                        <DivWithShadowAlternate className="p-1.5rem">
                                            {stripeCustomer.cards.length > 0 && (
                                                <div className={classNames({ 'mb-1rem': selectedPayment === 'new' })}>
                                                    <RadioWithoutForm
                                                        checked={selectedPayment === 'new'}
                                                        label="New Card or Wallet"
                                                        name="paymentSelection"
                                                        value="new"
                                                        onChange={() => setSelectedPayment('new')}
                                                    />
                                                </div>
                                            )}

                                            {selectedPayment === 'new' && stripeInstanceForCart && selectedPayment ? (
                                                <Elements
                                                    stripe={stripeInstanceForCart}
                                                    options={{
                                                        clientSecret: cart.payment_intent.client_secret,
                                                        appearance: {
                                                            variables: {
                                                                colorBackground:
                                                                    selectedPayment !== 'new'
                                                                        ? colors.ultraLightGray
                                                                        : 'transparent',
                                                                colorTextPlaceholder:
                                                                    selectedPayment !== 'new'
                                                                        ? colors.mediumGray
                                                                        : colors.azure,
                                                                // fontFamily: 'Red Hat Display, sans-serif',
                                                            },
                                                            rules,
                                                        },
                                                    }}
                                                >
                                                    <CartPaymentElementWrapper
                                                        paymentIntentSecret={cart?.payment_intent?.client_secret}
                                                        selectedPayment={selectedPayment}
                                                        setStripeCardComplete={setStripeCardComplete}
                                                        setStripeElements={setStripeElements}
                                                        stripeInstanceForCart={stripeInstanceForCart}
                                                    />
                                                </Elements>
                                            ) : null}
                                            {/*
                                        <div className="mt-0.75rem">
                                        <CardNameForm
                                        disabled={selectedPayment !== 'new'}
                                        onFirstNameChange={setFirstNameOnCard}
                                        onLastNameChange={setLastNameOnCard}
                                        />

                                        <AddressElement
                                        options={{
                                            mode: 'billing',
                                            readOnly: selectedPayment !== 'new',
                                        }}
                                        onChange={e => setSetStripeAddressComplete(e.complete)}
                                        />
                                        </div>
                                    */}
                                        </DivWithShadowAlternate>
                                    )}
                                </div>

                                <div className="mt-2rem">
                                    {cart.total !== 0 && (
                                        <div className="flex">
                                            <CheckboxWithoutForm
                                                checked={isAutoRenewChecked}
                                                text=""
                                                onCheckChange={checked => setIsAutoRenewChecked(checked)}
                                            >
                                                {savePaymentNotification.label}
                                            </CheckboxWithoutForm>
                                            <div className="ml-2">
                                                <TooltipInfo
                                                    color={colors.azure}
                                                    text={savePaymentNotification.tooltip}
                                                    tooltipClasses="w-2/5"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-2rem">
                                        <CheckboxWithoutForm
                                            checked={conditionsAccepted}
                                            onCheckChange={newValue => setConditionsAccepted(newValue)}
                                        >
                                            By making a purchase, you agree to ICSC's{' '}
                                            <a className="text-azure" href="/privacy-policy" target="_blank">
                                                privacy notice
                                            </a>
                                            ,{' '}
                                            <a className="text-azure" href="/terms-and-conditions" target="_blank">
                                                conditions of use
                                            </a>
                                            , and{' '}
                                            <a
                                                className="text-azure"
                                                href="/terms-and-conditions#:~:text=Maintain%20a%20professional%20level%20of,or%20harassing%20behavior%20and%20speech."
                                                target="_blank"
                                            >
                                                event code of conduct
                                            </a>
                                            .
                                        </CheckboxWithoutForm>
                                    </div>
                                </div>
                            </div>

                            {isScreenMinMd && (
                                <div className="col-span-3">
                                    <CartSummarySidebar hideDiscountsSection />
                                </div>
                            )}
                        </GridContainerStyled>
                    </div>
                </BodyContainerStyled>

                {isScreenMinMd && (
                    <FooterNavigation
                        nextButtonDisabled={isConfirmButtonDisabled}
                        nextLabel={`Confirm & Pay ${formatMoney(cart.total, cart.locale)}`}
                        previousLabel="Cart Details"
                        showPreviousCaret
                        onNextClick={() => checkOut()}
                        onPreviousClick={() => history.push('/cart/view')}
                    />
                )}

                {isScreenSmDown && (
                    <FooterMobileContainerStyled>
                        <ButtonPrimaryLarge
                            className="w-full"
                            disabled={isConfirmButtonDisabled}
                            onClick={() => checkOut()}
                        >
                            Confirm & Pay {formatMoney(cart.total, cart.locale)}
                        </ButtonPrimaryLarge>
                    </FooterMobileContainerStyled>
                )}
            </div>
        </Track>
    );
};

export default CartPaymentPageInner;
